import { Injectable } from "@angular/core";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
	ANDROID_BUNDLE_ID,
	ANDROID_MIN_VERSION_CODE,
	ANDROID_PLAY_STORE_URL,
	IOS_APP_STORE_URL,
	IOS_BUNDLE_ID
} from "@helpers/constants";
import { config } from "@config";

export class FirebaseConfig {
	public apiKey = "";
}

interface AndroidInfo {
	readonly androidPackageName: string;
	readonly androidFallbackLink: string;
	readonly androidMinPackageVersionCode?: string;
}

interface IOSInfo {
	readonly iosBundleId: string;
	readonly iosFallbackLink: string;
	readonly iosCustomScheme?: string;
	readonly iosIpadFallbackLink?: string;
	readonly iosIpadBundleId?: string;
	readonly iosAppStoreId?: string;
}

export interface SocialMetaTagInfo {
	readonly socialTitle: string;
	readonly socialDescription: string;
	readonly socialImageLink: string;
}

interface SuffixInfo {
	option: "SHORT" | "UNGUESSABLE";
}

interface DynamicLinkInfo {
	domainUriPrefix: string;
	link: string;
	androidInfo?: AndroidInfo;
	iosInfo?: IOSInfo;
	socialMetaTagInfo?: SocialMetaTagInfo;
}

interface DynamicLinkRequest {
	dynamicLinkInfo: DynamicLinkInfo;
	suffix: SuffixInfo;
}

interface DynamicLinkResponse {
	previewLink: string;
	shortLink: string;
}

@Injectable({
	providedIn: "root"
})
export class FirebaseService {
	private readonly apiKey: string = "";
	private readonly SHORTEN_LINK_URL: string = config.externalPaths["firebase"]["shortLinksUrl"];
	private readonly DOMAIN_URI_PREFIX: string = config.externalPaths["firebase"]["domainPrefix"];
	private readonly KEY_PARAM: string = "key";

	public constructor(
		{ apiKey }: FirebaseConfig,
		private http: HttpClient,
		private handler: HttpBackend
	) {
		this.apiKey = apiKey;
		this.http = new HttpClient(handler);
	}

	public shortenLink(link: string, socialInfo: SocialMetaTagInfo): Observable<string> {
		const requestBody: DynamicLinkRequest = {
			dynamicLinkInfo: {
				domainUriPrefix: this.DOMAIN_URI_PREFIX,
				link: link,
				androidInfo: {
					androidPackageName: ANDROID_BUNDLE_ID,
					androidFallbackLink: ANDROID_PLAY_STORE_URL,
					androidMinPackageVersionCode: ANDROID_MIN_VERSION_CODE
				},
				iosInfo: {
					iosFallbackLink: IOS_APP_STORE_URL,
					iosBundleId: IOS_BUNDLE_ID
				},
				socialMetaTagInfo: socialInfo
			},
			suffix: {
				option: "SHORT"
			}
		};
		return this.authenticatedPost(this.SHORTEN_LINK_URL, requestBody).pipe(
			map((res: DynamicLinkResponse) => res.shortLink)
		);
	}

	private authenticatedPost(url: string, body: Object): Observable<Object> {
		return this.http.post(this.appendApiKey(url), body);
	}

	private appendApiKey(requestUrl: string): string {
		return requestUrl + `?${this.KEY_PARAM}=${this.apiKey}`;
	}
}
